import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import toast from 'react-hot-toast';
import Skeleton from 'react-loading-skeleton';
import { Order } from 'shared/types/cart';
import Button from 'components/commercetools-ui/atoms/button';
import Checkbox from 'components/commercetools-ui/atoms/checkbox';
import Typography from 'components/commercetools-ui/atoms/typography';
import { useFormat } from 'helpers/hooks/useFormat';
import { useCart } from 'frontastic';

interface PreferenceData {
  isEmailSubscribed: boolean;
  isMailSubscribed: boolean;
  isSMSSubscribed: boolean;
  isPhoneSubscribed: boolean;
}

interface Props {
  data: PreferenceData;
  setPreferenceData: any;
  order: Order | undefined;
}

export const MarketingPreference = ({ data, setPreferenceData, order }: Props) => {
  const { formatMessage: formatMarketingMessage } = useFormat();
  const [loading, setLoading] = useState(false);
  const searchParams = useSearchParams();
  const { updateOrder } = useCart();

  const updateData = (name: string, value: boolean | string) => {
    setPreferenceData({ ...data, [name]: value });
  };

  const handleSubmit = (e: any) => {
    const orderId = searchParams.get('orderId');
    if (!orderId) return;
    setLoading(true);
    updateOrder(orderId, data)
      .then((res) => {
        toast.success(
          formatMarketingMessage({
            id: 'marketing.preference',
            defaultMessage: 'Marketing preference updated successfully.',
          }),
        );
        console.log('successfully updated preference', res);
      })
      .catch((err) => {
        console.log('something went wrong', err);
        toast.error(formatMarketingMessage({ id: 'went.wrong', defaultMessage: 'Sorry, Something went wrong.' }));
      })
      .finally(() => setLoading(false));
    e.preventDefault();
  };

  return (
    <div>
      <div className="flex w-[450] flex-col items-center justify-start bg-white px-16 py-12 md:px-24 md:py-12 lg:px-20 lg:py-48 xl:px-48">
        <div className="lg:w-[850px]">
          <div className="mb-20">
            <Typography
              id="marketing-preference"
              className="mb-20 scroll-mt-[300px] font-body text-20 font-bold uppercase md:text-23 xl:text-23"
              asSkeleton={!order}
            >
              Your Marketing Preferences
            </Typography>
            <Typography asSkeleton={!order} className="font-normal">
              In addition to your order, you have successfully registered to receive the latest news and offers from
              American Golf. You can update your preferences here.
            </Typography>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-20">
              <div className="flex items-start gap-16">
                {!order ? (
                  <Skeleton></Skeleton>
                ) : (
                  <>
                    <Checkbox
                      name="isEmailSubscribed"
                      id="email-marketing"
                      checked={data.isEmailSubscribed}
                      onChange={({ name, checked }) => updateData(name, checked)}
                    />
                    <div>
                      <Typography className="font-semibold text-secondary-black">Email:</Typography>
                      <Typography className="font-normal text-secondary-black">
                        I am happy to receive exclusive offers, product information and news by email.
                      </Typography>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-start gap-16">
                {!order ? (
                  <Skeleton></Skeleton>
                ) : (
                  <>
                    <Checkbox
                      name="isMailSubscribed"
                      id="post-marketing"
                      checked={data.isMailSubscribed}
                      onChange={({ name, checked }) => updateData(name, checked)}
                    />
                    <div>
                      <Typography className="font-semibold text-secondary-black">Post:</Typography>
                      <Typography className="font-normal text-secondary-black">
                        I am happy to receive exclusive offers, product information and news by post.
                      </Typography>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-start gap-16">
                {!order ? (
                  <Skeleton></Skeleton>
                ) : (
                  <>
                    <Checkbox
                      name="isPhoneSubscribed"
                      id="phone-marketing"
                      checked={data.isPhoneSubscribed}
                      onChange={({ name, checked }) => updateData(name, checked)}
                    />
                    <div>
                      <Typography className="font-semibold text-secondary-black">Phone:</Typography>
                      <Typography className="font-normal text-secondary-black">
                        I am happy to receive exclusive offers, product information and news by phone.
                      </Typography>
                    </div>
                  </>
                )}
              </div>
              <div className="flex items-start gap-16">
                {!order ? (
                  <Skeleton></Skeleton>
                ) : (
                  <>
                    <Checkbox
                      name="isSMSSubscribed"
                      id="sms-marketing"
                      checked={data.isSMSSubscribed}
                      onChange={({ name, checked }) => updateData(name, checked)}
                    />
                    <div>
                      <Typography className="font-semibold text-secondary-black">SMS:</Typography>
                      <Typography className="font-normal text-secondary-black">
                        I am happy to receive exclusive offers, product information and news by SMS.
                      </Typography>
                    </div>
                  </>
                )}
              </div>
            </div>
            {!order ? (
              <Skeleton></Skeleton>
            ) : (
              <Button className="mt-20 w-full cursor-pointer text-16 md:w-[225px]" variant="primary" loading={loading}>
                Submit
              </Button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default MarketingPreference;
